import type { ImportCommitResult, ImportPreview } from '@jmc/shared';
import { formatCount, formatDateTime } from '@jmc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, apiDownload, ApiRequestError, apiUpload } from '../lib/api.js';
import { useI18n } from '../lib/i18n.js';
import {
  cx,
  ErrorState,
  InlineError,
  Mono,
  PageHeader,
  Panel,
  Spinner,
  Toast,
} from '../components/ui.js';

interface Batch {
  id: number;
  original_name: string;
  form_kind: 'GENERAL' | 'PROPERTY';
  status: string;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  inserted_rows: number;
  created_at: string;
  uploaded_by_name: string;
}

export function ImportPage() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);

  const [kind, setKind] = useState<'GENERAL' | 'PROPERTY'>('PROPERTY');
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const batches = useQuery({
    queryKey: ['import-batches'],
    queryFn: () => api<{ batches: Batch[] }>('/imports'),
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      // The kind field must precede the file part: the server reads fields
      // from the same multipart stream as it consumes the upload.
      form.append('kind', kind);
      form.append('file', file);
      return apiUpload<ImportPreview>('/imports/stage', form);
    },
    onSuccess: (result) => {
      setPreview(result);
      setError(null);
    },
    onError: (caught) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('common.error')),
  });

  const commit = useMutation({
    mutationFn: (batchId: number) =>
      api<ImportCommitResult>(`/imports/${batchId}/commit`, { method: 'POST' }),
    onSuccess: (result) => {
      setPreview(null);
      setToast(`${t('import.done')}: ${formatCount(result.inserted)}`);
      void queryClient.invalidateQueries({ queryKey: ['import-batches'] });
      void queryClient.invalidateQueries({ queryKey: ['files'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (caught) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('common.error')),
  });

  const discard = useMutation({
    mutationFn: (batchId: number) =>
      api(`/imports/${batchId}/discard`, { method: 'POST' }),
    onSuccess: () => {
      setPreview(null);
      setError(null);
    },
  });

  return (
    <>
      <PageHeader title={t('import.title')} />

      <div className="grid gap-4 lg:grid-cols-2">
        {/* Step 1 */}
        <Panel bodyClassName="px-5 py-5">
          <Step number={1} title={t('import.step1')} hint={t('import.step1Hint')} />
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() =>
                void apiDownload(
                  '/imports/template?kind=GENERAL',
                  'JMC-Form-1-General-Files-Template.xlsx',
                )
              }
            >
              <DownloadIcon />
              {t('import.form1')}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() =>
                void apiDownload(
                  '/imports/template?kind=PROPERTY',
                  'JMC-Form-2-Property-Files-Template.xlsx',
                )
              }
            >
              <DownloadIcon />
              {t('import.form2')}
            </button>
          </div>
        </Panel>

        {/* Step 2 */}
        <Panel bodyClassName="px-5 py-5">
          <Step number={2} title={t('import.step2')} hint={t('import.step2Hint')} />

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <div className="flex rounded-[5px] border border-line-strong p-0.5">
              {(['GENERAL', 'PROPERTY'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setKind(option)}
                  aria-pressed={kind === option}
                  className={cx(
                    'rounded-[3px] px-3 py-1 text-[0.8125rem] font-medium transition-colors',
                    kind === option
                      ? 'bg-navy-700 text-white'
                      : 'text-ink-500 hover:text-navy-700',
                  )}
                >
                  {option === 'GENERAL' ? 'Form 1' : 'Form 2'}
                </button>
              ))}
            </div>

            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls,.xlsm,.csv"
              className="sr-only"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) upload.mutate(file);
                event.target.value = '';
              }}
            />
            <button
              type="button"
              className="btn-primary"
              onClick={() => inputRef.current?.click()}
              disabled={upload.isPending}
            >
              {upload.isPending && <Spinner />}
              {upload.isPending ? t('import.checking') : t('import.choose')}
            </button>
          </div>
        </Panel>
      </div>

      {error && <div className="mt-4"><InlineError message={error} /></div>}

      {preview && (
        <Panel className="mt-4" title={preview.fileName} bodyClassName="px-5 py-5">
          <div className="grid gap-3 sm:grid-cols-5">
            <Metric label={t('import.totalRows')} value={preview.totalRows} />
            <Metric label={t('import.validRows')} value={preview.validRows} tone="green" />
            <Metric label={t('import.errorRows')} value={preview.errorRows} tone="red" />
            <Metric label={t('import.duplicateRows')} value={preview.duplicateRows} tone="amber" />
            <Metric
              label={t('import.newLocations')}
              value={preview.newLocations}
              tone={preview.newLocations > 0 ? 'amber' : 'plain'}
            />
          </div>

          {preview.newLocations > 0 && (
            <p className="mt-3 rounded-[5px] border border-amber-line bg-amber-bg px-3 py-2 text-[0.8125rem] text-amber-ink">
              {t('import.newLocationsHint')}
            </p>
          )}

          {preview.errors.length > 0 && (
            <div className="mt-5">
              <h3 className="mb-2 font-medium">{t('import.problems')}</h3>
              <div className="max-h-72 overflow-auto rounded-[5px] border border-line">
                <table className="data-table">
                  <thead className="sticky top-0">
                    <tr>
                      <th className="w-20">{t('import.row')}</th>
                      <th className="w-44">{t('import.column')}</th>
                      <th>{t('import.problem')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.errors.map((problem, index) => (
                      <tr key={`${problem.rowNo}-${index}`}>
                        <td className="tabular">{problem.rowNo}</td>
                        <td className="text-ink-500">{problem.column ?? '—'}</td>
                        <td>
                          {problem.message}
                          {problem.rawValue && (
                            <Mono className="ml-2 text-ink-400">
                              “{problem.rawValue}”
                            </Mono>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="mt-5 flex flex-wrap justify-end gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => discard.mutate(preview.batchId)}
              disabled={discard.isPending}
            >
              {t('import.discard')}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => commit.mutate(preview.batchId)}
              disabled={commit.isPending || preview.validRows === 0}
            >
              {commit.isPending && <Spinner />}
              {commit.isPending
                ? t('import.committing')
                : `${t('import.commit')} (${formatCount(preview.validRows)})`}
            </button>
          </div>
        </Panel>
      )}

      <Panel className="mt-4" title={t('import.history')} bodyClassName="overflow-x-auto">
        {batches.isError && (
          <div className="p-4">
            <ErrorState error={batches.error} onRetry={() => void batches.refetch()} />
          </div>
        )}
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('import.sheet')}</th>
              <th>{t('field.status')}</th>
              <th className="hidden sm:table-cell">{t('import.validRows')}</th>
              <th className="hidden md:table-cell">{t('users.name')}</th>
              <th className="hidden lg:table-cell">{t('audit.when')}</th>
            </tr>
          </thead>
          <tbody>
            {(batches.data?.batches ?? []).map((batch) => (
              <tr key={batch.id}>
                <td className="max-w-[16rem] truncate">{batch.original_name}</td>
                <td>
                  <span
                    className={cx(
                      'badge',
                      batch.status === 'COMMITTED'
                        ? 'border-green-line bg-green-bg text-green-ink'
                        : 'border-line-strong bg-paper text-ink-500',
                    )}
                  >
                    {batch.status}
                  </span>
                </td>
                <td className="hidden tabular sm:table-cell">
                  {formatCount(batch.inserted_rows || batch.valid_rows)} / {formatCount(batch.total_rows)}
                </td>
                <td className="hidden md:table-cell">{batch.uploaded_by_name}</td>
                <td className="hidden whitespace-nowrap lg:table-cell">
                  {formatDateTime(batch.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </>
  );
}

function Step({ number, title, hint }: { number: number; title: string; hint: string }) {
  return (
    <div className="flex gap-3">
      <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-navy-700 text-[0.75rem] font-semibold text-white">
        {number}
      </span>
      <div>
        <h2 className="font-semibold">{title}</h2>
        <p className="mt-0.5 text-ink-500">{hint}</p>
      </div>
    </div>
  );
}

const METRIC_TONE: Record<string, string> = {
  plain: 'text-ink-900',
  green: 'text-green-ink',
  red: 'text-red-ink',
  amber: 'text-amber-ink',
};

function Metric({
  label,
  value,
  tone = 'plain',
}: {
  label: string;
  value: number;
  tone?: keyof typeof METRIC_TONE;
}) {
  return (
    <div className="rounded-[5px] border border-line px-3 py-2.5">
      <p className="text-[0.8125rem] text-ink-500">{label}</p>
      <p className={cx('mt-0.5 text-xl font-semibold tabular', METRIC_TONE[tone])}>
        {formatCount(value)}
      </p>
    </div>
  );
}

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M10 3v8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      <path
        d="m6.75 8.25 3.25 3.25 3.25-3.25"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3.5 13.5v2.25a.75.75 0 0 0 .75.75h11.5a.75.75 0 0 0 .75-.75V13.5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}
